import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/helpers/auth.service';
import { CoreService } from 'src/app/core/helpers/core.service';

@Component({
  selector: 'loyaltic-rewards-carousel',
  templateUrl: './rewards-carousel.component.html',
  styleUrls: ['./rewards-carousel.component.scss']
})
export class RewardsCarouselComponent implements OnInit, OnDestroy {
  readonly fallbackImageUrl = 'https://res.cloudinary.com/lojaali/image/upload/q_auto/v1641884290/SWEPEPSIMAX24/default.jpg';
  readonly autoSlideIntervalMs = 5000;
  readonly swipeThresholdPx = 40;

  culture = '';
  rewards: any[] = [];
  userId = '';
  currentIndex = 0;
  visibleItems = 3;
  autoSlideTimer: any = null;
  touchStartX: number | null = null;
  touchCurrentX: number | null = null;

  constructor(
    private coreService: CoreService,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    this.culture = translate.getDefaultLang();
    this.userId = authService.getUserId();
    this.updateVisibleItems();
  }

  ngOnInit() {
    this.getRewards();
  }

  ngOnDestroy() {
    this.stopAutoSlide();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateVisibleItems();
    this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
  }

  getRewards() {
    this.coreService.getRewards(this.culture, this.userId).then((response: any) => {
      const rewards = response && response.result && response.result.rewards ? response.result.rewards : [];
      this.rewards = rewards;
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      this.restartAutoSlide();
    });
  }

  get trackTransform() {
    return 'translateX(-' + (this.currentIndex * (100 / this.visibleItems)) + '%)';
  }

  get maxIndex() {
    return Math.max(this.rewards.length - this.visibleItems, 0);
  }

  get canSlide() {
    return this.rewards.length > this.visibleItems;
  }

  prev() {
    if (!this.canSlide) {
      return;
    }

    this.currentIndex = this.currentIndex === 0 ? this.maxIndex : this.currentIndex - 1;
    this.restartAutoSlide();
  }

  next() {
    if (!this.canSlide) {
      return;
    }

    this.currentIndex = this.currentIndex >= this.maxIndex ? 0 : this.currentIndex + 1;
    this.restartAutoSlide();
  }

  getRewardTitle(reward: any) {
    return (reward && (reward.longTitle || reward.title)) || '';
  }

  onTouchStart(event: TouchEvent) {
    if (!this.canSlide || event.touches.length !== 1) {
      return;
    }

    this.touchStartX = event.touches[0].clientX;
    this.touchCurrentX = this.touchStartX;
    this.stopAutoSlide();
  }

  onTouchMove(event: TouchEvent) {
    if (this.touchStartX === null || event.touches.length !== 1) {
      return;
    }

    this.touchCurrentX = event.touches[0].clientX;
  }

  onTouchEnd() {
    if (this.touchStartX === null || this.touchCurrentX === null) {
      this.restartAutoSlide();
      this.resetTouchState();
      return;
    }

    const deltaX = this.touchCurrentX - this.touchStartX;

    if (Math.abs(deltaX) >= this.swipeThresholdPx) {
      if (deltaX < 0) {
        this.advance(true);
      } else {
        this.advance(false);
      }
    }

    this.resetTouchState();
    this.restartAutoSlide();
  }

  onTouchCancel() {
    this.resetTouchState();
    this.restartAutoSlide();
  }

  private updateVisibleItems() {
    this.visibleItems = window.innerWidth < 992 ? 1 : 3;
  }

  private advance(forward: boolean) {
    if (!this.canSlide) {
      return;
    }

    if (forward) {
      this.currentIndex = this.currentIndex >= this.maxIndex ? 0 : this.currentIndex + 1;
      return;
    }

    this.currentIndex = this.currentIndex === 0 ? this.maxIndex : this.currentIndex - 1;
  }

  private restartAutoSlide() {
    this.stopAutoSlide();

    if (!this.canSlide) {
      return;
    }

    this.autoSlideTimer = setInterval(() => {
      this.advance(true);
    }, this.autoSlideIntervalMs);
  }

  private stopAutoSlide() {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  private resetTouchState() {
    this.touchStartX = null;
    this.touchCurrentX = null;
  }
}
